import { c as _c } from "react/compiler-runtime";
import { faChevronDown, faChevronRight } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { PayPalButtons, PayPalScriptProvider } from '@paypal/react-paypal-js';
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { loadStripe, Stripe, StripeElementsOptionsMode } from '@stripe/stripe-js';
import { DateTime } from 'luxon';
import React, { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Link as ReachLink } from 'react-router-dom';
import axios from 'shared/anaxios';
import Button from 'shared/button';
import EmptyState from 'shared/empty-state';
import { Box, Flex, Grid } from 'shared/grid';
import Tippy from 'shared/tooltip';
import Loader from 'shared/loader';
import { fetchBooking } from 'shared/redux/bookings/actions';
import settings from 'shared/settings';
import { CroppedText, Heading, Link, Text } from 'shared/typography';
import { toBase58 } from 'shared/utils/b58';
import { currencyFormat } from 'shared/utils/currency';
import { useAppDispatch, useAppSelector } from '../../store';
import { BorderBox } from '../shared';
import { useCantCancelBookingReason } from './hooks';
import { Field, Form } from 'react-final-form';
import anaxios from 'shared/anaxios';
import FormError from 'shared/form-error';
import { formPost, payfastRedirect, paymentMeta } from 'shared/payments';
export const StripeCheckoutForm = t0 => {
  const $ = _c(17);
  const {
    currency,
    amount,
    bookingId: t1,
    invoiceId: t2,
    clientId: t3,
    locationId: t4,
    paymentMethodId
  } = t0;
  const bookingId = t1 === undefined ? null : t1;
  const invoiceId = t2 === undefined ? null : t2;
  const clientId = t3 === undefined ? null : t3;
  const locationId = t4 === undefined ? null : t4;
  const stripe = useStripe();
  const elements = useElements();
  const [errorMessage, setErrorMessage] = useState("");
  let t5;
  if ($[0] !== amount || $[1] !== bookingId || $[2] !== clientId || $[3] !== currency || $[4] !== elements || $[5] !== invoiceId || $[6] !== locationId || $[7] !== paymentMethodId || $[8] !== stripe) {
    t5 = async values => {
      if (elements == null || stripe == null) {
        return;
      }
      const {
        error: submitError
      } = await elements.submit();
      if (submitError?.message) {
        setErrorMessage(submitError.message);
        return;
      }
      const res = await anaxios.post(`${settings.api1Root}/stripe-create-payment-intent`, {
        amount: amount * 100,
        currency,
        booking_id: bookingId,
        invoice_id: invoiceId,
        client_id: clientId,
        location_id: locationId,
        payment_method_id: paymentMethodId
      });
      const {
        client_secret: clientSecret
      } = await res.data;
      const {
        error
      } = await stripe.confirmPayment({
        elements,
        clientSecret,
        confirmParams: {
          return_url: window.location.href
        }
      });
      if (error) {
        setErrorMessage(error.message);
      }
    };
    $[0] = amount;
    $[1] = bookingId;
    $[2] = clientId;
    $[3] = currency;
    $[4] = elements;
    $[5] = invoiceId;
    $[6] = locationId;
    $[7] = paymentMethodId;
    $[8] = stripe;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const onSubmit = t5;
  let t6;
  if ($[10] !== elements || $[11] !== errorMessage || $[12] !== stripe) {
    t6 = t7 => {
      const {
        handleSubmit,
        submitting
      } = t7;
      return <Grid as="form" onSubmit={handleSubmit}><Field name="paymentElement">{_temp}</Field><Button type="submit" disabled={!stripe || !elements || submitting}>Pay</Button>{errorMessage && <FormError>{errorMessage}</FormError>}</Grid>;
    };
    $[10] = elements;
    $[11] = errorMessage;
    $[12] = stripe;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== onSubmit || $[15] !== t6) {
    t7 = <Form onSubmit={onSubmit} render={t6} />;
    $[14] = onSubmit;
    $[15] = t6;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  return t7;
};
export default function BookingPayView({
  booking,
  location,
  canCancel,
  isOwner,
  setShowCancelModal
}) {
  const business = useAppSelector(state => state.businesses.data[state.public.businessId]);
  const cantCancelReason = useCantCancelBookingReason(canCancel, business?.settings.disable_changes, booking?.services, isOwner);
  const dispatch = useAppDispatch();
  const [paymentData, setPaymentData] = React.useState(null);
  const [status, setStatus] = React.useState('loading');
  const [isProcessingPayment, setIsProcessingPayment] = React.useState(false);
  const fetchPaymentDetails = React.useCallback(() => {
    const request = () => axios.get(`${settings.api2Root}/booking-payment-data/${booking.id}`, {
      skipErrorResponseIntercept: true,
      skipErrorReportOnCode: {
        404: true
      }
    });
    const retry = ms => new Promise((resolve, reject) => setTimeout(() => request().then(v => resolve(v), e => reject(e)), ms));
    request().catch(() => retry(2000)).catch(() => retry(4000)).then(
    //@ts-ignore
    ({
      data
    }) => {
      setPaymentData(data);
      setStatus('done');
    }, () => {
      Sentry.captureMessage('Could not find booking payment data', 'error');
      setStatus('error');
    });
  }, [booking.id]);
  React.useEffect(() => {
    if (!booking.id) {
      setStatus('done');
      return;
    }
    fetchPaymentDetails();
  }, [booking.id, fetchPaymentDetails]);
  const stripePk = React.useMemo(() => paymentData?.payment_methods?.find(p => p.type === 'stripe' && p.provider_data)?.provider_data?.publishable_key, [paymentData]);
  const stripePromise: Promise<Stripe> | null = React.useMemo(() => {
    if (stripePk) {
      return loadStripe(stripePk);
    }
  }, [stripePk]);

  ////////////////////////

  const serviceData = useAppSelector(state_0 => {
    return state_0.services.data;
  });
  const billableItemData = useAppSelector(state_1 => state_1.billableItems.data);
  const invoiceSettings = useAppSelector(state_2 => state_2.invoiceSettings.data[state_2.public.businessId]);
  const items = React.useMemo(() => {
    return booking.services.map(({
      start,
      end,
      service_id,
      service_name
    }) => {
      const service = serviceData[service_id];
      const serviceLocation = service?.locations?.find(sl => sl.location_id === booking.location_id);
      if (!serviceLocation) {
        return null;
      }
      const duration = (DateTime.fromISO(end).valueOf() - DateTime.fromISO(start).valueOf()) / 60000;
      const sd = serviceLocation.service_durations.find(v_0 => v_0.duration === duration);
      if (!sd) {
        return null;
      }
      if (sd.pricing_model === 'hidden') {
        return null;
      }
      const tax_rate = invoiceSettings ? invoiceSettings.location_settings[invoiceSettings.is_location_specific ? booking.location_id : '*'].default_tax_rate : 0;
      if (sd.pricing_model === 'simple') {
        return {
          description: service_name,
          price: sd.price * (1 + tax_rate / 100)
        };
      }
      return {
        description: service_name,
        price: sd.billable_items.reduce((total, {
          billable_item_id,
          quantity
        }) => {
          const item = billableItemData[billable_item_id];
          if (!item) {
            return total;
          }
          return total + (quantity || 0) * (item.unit_price[booking.location_id] || 0);
        }, 0) * (1 + tax_rate / 100)
      };
    });
  }, [booking.services, invoiceSettings, billableItemData, serviceData, booking.location_id]);
  const [openAccordion, setOpenAccordion] = React.useState(null);
  const {
    total_amount,
    paid_amount,
    total_due,
    currency,
    payment_methods
  } = paymentData || {};
  const stripeOptions: StripeElementsOptionsMode = React.useMemo(() => ({
    mode: 'payment',
    amount: total_due * 100,
    // Stripe expects the amount in cents
    currency: currency?.toLowerCase()
  }), [total_due, currency]);
  if (status === 'loading') {
    return <Loader />;
  }
  if (status === 'error') {
    return <EmptyState heading="There is a delay in generating your invoice. Please wait a moment and click retry." callToAction={<Button onClick={fetchPaymentDetails}>Retry</Button>} />;
  }
  return <Box data-sentry-element="Box" data-sentry-component="BookingPayView" data-sentry-source-file="pay.tsx">
      <Grid gridTemplateAreas={[`
          'summary'
          'methods'
        `, `
          'methods summary'
        `]} gridTemplateColumns={['1fr', '1fr 300px']} data-sentry-element="Grid" data-sentry-source-file="pay.tsx">
        {total_due > 0 ? <Box gridArea="methods">
            <BorderBox p={0}>
              {payment_methods.some(p_2 => paymentMeta[p_2.type]?.integrated) ? <>
                  <Box px={4} py={8} borderBottomWidth="1px" borderBottomStyle="solid" borderBottomColor="gray.1">
                    <Heading fontSize={2} fontWeight={600}>
                      Choose a payment method
                    </Heading>
                  </Box>
                  {payment_methods.filter(p_0 => p_0.is_available_to_clients).filter(p_1 => paymentMeta[p_1.type]?.integrated).map(({
              id,
              provider_data: {
                form_data,
                host,
                qrcode_url,
                qrcode_svg_url,
                client_id,
                payment_url
              },
              type,
              instructions
            }, idx) => <Box key={idx}>
                          <Grid p={4} gridGap={4} gridTemplateColumns={'auto 1fr auto'} sx={{
                cursor: 'pointer',
                '&:hover, &:active': {
                  backgroundColor: 'gray.1'
                }
              }} onClick={() => {
                if (type === 'payfast') {
                  setOpenAccordion(null);
                  payfastRedirect(host, form_data);
                } else if (type == 'wigwag') {
                  formPost(payment_url, {
                    invoice_id: paymentData?.invoice?.id,
                    booking_id: booking.id,
                    payment_method_id: id
                  });
                } else if (type == 'yoco') {
                  formPost(payment_url, {
                    invoice_id: paymentData?.invoice?.id,
                    booking_id: booking.id,
                    payment_method_id: id
                  });
                } else {
                  setOpenAccordion(openAccordion === idx ? null : idx);
                }
              }}>
                            <Flex alignItems="center">
                              {paymentMeta[type].icon}
                            </Flex>

                            <Flex alignItems="center">
                              <CroppedText fontFamily="heading" fontSize={2}>
                                {paymentMeta[type].name}
                              </CroppedText>
                            </Flex>

                            <Flex alignItems="center">
                              {paymentMeta[type].inPage && <FontAwesomeIcon icon={openAccordion === idx ? faChevronDown : faChevronRight} />}
                            </Flex>
                          </Grid>
                          <PayPalScriptProvider options={{
                clientId: client_id,
                currency,
                intent: 'capture'
              }}>
                            {openAccordion === idx && type == 'paypal' && <Box p={4}>
                                <PayPalButtons style={{
                    layout: 'horizontal'
                  }} createOrder={(data_0, actions) => {
                    setIsProcessingPayment(true);
                    return actions.order.create({
                      intent: 'CAPTURE',
                      purchase_units: [{
                        amount: {
                          currency_code: currency,
                          value: total_due.toString()
                        }
                      }],
                      application_context: {
                        shipping_preference: 'NO_SHIPPING'
                      }
                    });
                  }} onApprove={(details, data_1) => {
                    setIsProcessingPayment(true);
                    return axios.post(`${settings.api2Root}/client-paypal-transaction-complete/${id}`, {
                      order_id: details.orderID,
                      booking_id: booking.id
                    }).then(() => {
                      setTimeout(() => {
                        dispatch(fetchBooking(booking.id)).then(() => setIsProcessingPayment(false));
                      }, 2000);
                    }).catch(() => setIsProcessingPayment(false));
                  }} />
                              </Box>}
                          </PayPalScriptProvider>

                          {openAccordion === idx && type == 'stripe' && <Box p={4}>
                              <Elements stripe={stripePromise} options={stripeOptions}>
                                <StripeCheckoutForm paymentMethodId={id} currency={currency} amount={total_due} bookingId={booking.id} invoiceId={paymentData?.invoice?.id} />
                              </Elements>
                            </Box>}

                          {openAccordion === idx && type == 'snapscan' && <Box p={4}>
                              <Link href={qrcode_url}>
                                <img alt="qr code" src={qrcode_svg_url} />
                              </Link>
                            </Box>}

                          {openAccordion === idx && type == 'eft' && <Grid p={4}>
                              {instructions.split('\n').map((v_1, idx_0) => <CroppedText key={idx_0}>{v_1}</CroppedText>)}
                            </Grid>}
                        </Box>)}
                </> : <EmptyState minHeight="323px" heading={`${business.name} is not accepting online payments at the
              moment.`} callToAction={<Text>
                      Please view your invoice for further instructions or{' '}
                      <Link as={ReachLink} to={`/contact` + (location['id'] ? `?lid=${location['id']}` : '')}>
                        contact us
                      </Link>
                    </Text>} />}
            </BorderBox>
          </Box> : <Box gridArea="methods">
            <BorderBox textAlign="center">
              <CroppedText fontSize={2}>
                Your payment was received. Thank you.
              </CroppedText>
            </BorderBox>
          </Box>}
        <Box gridArea="summary" data-sentry-element="Box" data-sentry-source-file="pay.tsx">
          <BorderBox p={0} data-sentry-element="BorderBox" data-sentry-source-file="pay.tsx">
            <Box px={4} py={8} borderBottomWidth="1px" borderBottomStyle="solid" borderBottomColor="gray.1" data-sentry-element="Box" data-sentry-source-file="pay.tsx">
              <Heading fontSize={2} fontWeight={600} data-sentry-element="Heading" data-sentry-source-file="pay.tsx">
                Summary
              </Heading>
            </Box>

            {items.filter(v_2 => v_2).map(({
            description,
            price
          }, idx_1) => <Flex justifyContent="space-between" p={4} key={idx_1}>
                  <CroppedText flex="1 1 0">{description}</CroppedText>
                  <CroppedText>{currencyFormat(price, currency)}</CroppedText>
                </Flex>)}

            {paid_amount > 0 && <Flex justifyContent="space-between" p={4}>
                <CroppedText>Paid</CroppedText>
                <CroppedText>
                  {currencyFormat(paid_amount, currency)}
                </CroppedText>
              </Flex>}

            <Flex justifyContent="space-between" px={4} py={8} borderTopWidth="1px" borderTopStyle="solid" borderTopColor="gray.1" data-sentry-element="Flex" data-sentry-source-file="pay.tsx">
              <CroppedText fontWeight={600} data-sentry-element="CroppedText" data-sentry-source-file="pay.tsx">Total due</CroppedText>
              <CroppedText fontWeight={600} data-sentry-element="CroppedText" data-sentry-source-file="pay.tsx">
                {currencyFormat(total_due, currency)}
              </CroppedText>
            </Flex>

            <Box p={4} borderTopWidth="1px" borderTopStyle="solid" borderTopColor="gray.1" data-sentry-element="Box" data-sentry-source-file="pay.tsx">
              <Tippy disabled={canCancel || booking.status == 'cancelled'} content={cantCancelReason} data-sentry-element="Tippy" data-sentry-source-file="pay.tsx">
                <Box data-sentry-element="Box" data-sentry-source-file="pay.tsx">
                  <Button color="alert" variant="outlined" onClick={() => setShowCancelModal(true)} width="100%" disabled={!canCancel} data-sentry-element="Button" data-sentry-source-file="pay.tsx">
                    <FormattedMessage id="Cancel booking" defaultMessage="Cancel booking" data-sentry-element="FormattedMessage" data-sentry-source-file="pay.tsx" />
                  </Button>
                </Box>
              </Tippy>
            </Box>
            <Box pb={4} pl={3} pr={3} data-sentry-element="Box" data-sentry-source-file="pay.tsx">
              <Button as="a" variant="outlined" color="gray" rel="noopener noreferrer" target="_blank" href={settings.bypassPWAScope(`/i/${toBase58(paymentData?.invoice?.id)}?&download=false`)} data-sentry-element="Button" data-sentry-source-file="pay.tsx">
                View invoice
              </Button>
            </Box>
          </BorderBox>
        </Box>
      </Grid>
    </Box>;
}
function _temp() {
  return <PaymentElement data-sentry-element="PaymentElement" data-sentry-component="_temp" data-sentry-source-file="pay.tsx" />;
}